import type { Persona } from "@/types/persona";
import heroImage from "@/assets/personas/physiotherapist-real3.jpg";

export const physiotherapists: Persona = {
  slug: "physiotherapists",
  profession: { hu: "gyógytornász", en: "physiotherapist" },
  professionPlural: {
    hu: "gyógytornászok és manuálterapeuták",
    en: "physiotherapists and manual therapists",
  },
  professionGenitive: { hu: "gyógytornászként", en: "as a physiotherapist" },
  heroH1: {
    hu: "Több páciens, kevesebb papírmunka. Élő oldal ma este.",
    en: "More patients, less paperwork. Live site tonight.",
  },
  heroSub: {
    hu: "AI weboldal, online foglalás, páciens-dokumentáció és kezelési előzmény: egy platformon, szóló gyógytornászoknak és klinikáknak.",
    en: "AI website, online booking, patient cards and treatment tracking: one platform for solo physios and clinics.",
  },
  heroImage,
  heroImageAlt: "Physiotherapist performing manual therapy",
  chatExamplePrompt: {
    hu: "Foglalj egy első konzultációt, fájdalom-felmérés",
    en: "Book a first consultation, pain assessment",
  },
  chatExampleResponse: {
    hu: "Felvettem az új páciens-kartont és bejegyeztem 60 perc fájdalom-felmérésre. A páciensnek visszaigazolást és előzetes kérdőívet küldtem.",
    en: "Created the new patient card and booked a 60-minute pain assessment. Confirmation and intake questionnaire sent to the patient.",
  },
  accentColor: "emerald",

  defaultMode: "both-equal",
  teamSize: { min: 1, max: 10 },
  soloLabel: { hu: "Szóló gyógytornász", en: "Solo physiotherapist" },
  teamLabel: {
    hu: "Klinika több terapeutával",
    en: "Clinic with multiple therapists",
  },

  soloDayInLife: [
    {
      time: "08:00",
      what: {
        hu: "Megnyitod a napot: 6 páciens, 1 új. Az előzetes kérdőíveket már beküldték.",
        en: "Open the day: 6 patients, 1 new. Intake questionnaires already submitted.",
      },
    },
    {
      time: "10:00",
      what: {
        hu: "Új lead a Google-ról derékfájással. Az AI visszaigazolt és lefoglalta a felmérést.",
        en: "New lead from Google with back pain. AI confirmed and booked the assessment.",
      },
    },
    {
      time: "13:00",
      what: {
        hu: "Manuálterápiás kezelés Annával. A kartonján látod a 3 hetes haladást és a házi gyakorlatokat.",
        en: "Manual therapy with Anna. Her card shows the 3-week progress and home exercises.",
      },
    },
    {
      time: "16:00",
      what: {
        hu: "Egy páciens lemond. Az AI értesíti a várólistát, telik a slot.",
        en: "A patient cancels. AI notifies the waitlist: slot filled.",
      },
    },
    {
      time: "19:00",
      what: {
        hu: "Napzárás: 8 kezelés, 96 000 Ft bevétel, 2 új beutaló.",
        en: "End of day: 8 treatments, 96,000 HUF revenue, 2 new referrals.",
      },
    },
  ],
  teamDayInLife: [
    {
      time: "08:00",
      what: {
        hu: "Klinika dashboard: 4 terapeutád 28 időpontot ad ma. Mindenki naptára átláthatóan.",
        en: "Clinic dashboard: your 4 therapists have 28 appointments today. All calendars at a glance.",
      },
    },
    {
      time: "10:30",
      what: {
        hu: "Új páciens vállproblémával: az AI a megfelelő specialistához irányítja (Eszti vállrehabra specializálódott).",
        en: "New patient with shoulder issue: AI routes them to the right specialist (Eszti is shoulder rehab).",
      },
    },
    {
      time: "13:00",
      what: {
        hu: "Egy terapeuta beteg. Az AI áthelyezi 5 páciensét másik kollégához, mindenkit értesít.",
        en: "A therapist is sick. AI moves their 5 patients to another colleague, notifies everyone.",
      },
    },
    {
      time: "16:00",
      what: {
        hu: "Az AI mutatja: Bence sport-rehabilitációs mutatója +22%. Megmutatod a csapatnak a folyamatait.",
        en: "AI shows: Bence's sports-rehab conversion is +22%. You share his process with the team.",
      },
    },
    {
      time: "19:00",
      what: {
        hu: "Páciens-elégedettségi pontszámok automatikusan begyűjtve, AI heti riport készül.",
        en: "Patient satisfaction scores collected automatically, AI weekly report ready.",
      },
    },
    {
      time: "22:00",
      what: {
        hu: "Bevétel terapeutánként, jutalék-elszámolás, számlák kiállítva, könyvelő-barát formátumban.",
        en: "Revenue per therapist, commission, invoices issued in accountant-friendly format.",
      },
    },
  ],

  soloFeatures: [
    {
      icon: "Calendar",
      title: { hu: "Minden egy fiókban", en: "Everything in one account" },
      body: {
        hu: "Naptár, páciens-karton, kezelés-előzmény, számla: egyetlen rendszerben, váltogatás nélkül.",
        en: "Calendar, patient cards, treatment history, invoices: in one system, no switching.",
      },
    },
    {
      icon: "Bot",
      title: {
        hu: "Online foglalás + kérdőív",
        en: "Online booking + questionnaire",
      },
      body: {
        hu: "Új páciens online lefoglalja a felmérést, kitölti az anamnézis-lapot: minden adat a kartonjában landol, mire bejön.",
        en: "New patient books the assessment online and fills out the anamnesis: everything lands in their card before they arrive.",
      },
    },
    {
      icon: "Bell",
      title: { hu: "Automata emlékeztetők", en: "Automatic reminders" },
      body: {
        hu: "Email és WhatsApp emlékeztető 24 órával előtte. Házi gyakorlatok e-mailben kiküldve.",
        en: "Email and WhatsApp reminders 24 hours ahead. Home exercises emailed automatically.",
      },
    },
    {
      icon: "Wallet",
      title: {
        hu: "Online + helyszíni fizetés",
        en: "Online + in-person payments",
      },
      body: {
        hu: "Beépített online kártya, Apple/Google Pay foglaláskor; helyszínen készpénz vagy POS is rögzíthető: minden tranzakció egy felületen, automata számlával.",
        en: "Built-in online card, Apple/Google Pay at booking; in-person cash or POS also tracked: every transaction in one place with auto-invoicing.",
      },
    },
  ],
  teamFeatures: [
    {
      icon: "Users",
      title: {
        hu: "Korlátlan terapeuta, egy ár",
        en: "Unlimited therapists, one price",
      },
      body: {
        hu: "1 vagy 10 fő: ugyanaz a havidíj. Mindenki saját naptárral, páciens-listával, specialitással.",
        en: "1 or 10: same monthly fee. Each with their own calendar, patient list, specialty.",
      },
    },
    {
      icon: "ShieldCheck",
      title: { hu: "Jogosultság-kezelés", en: "Role-based access" },
      body: {
        hu: "Tulaj, asszisztens, terapeuta: egészségügyi adatokhoz csak az érintett terapeuta fér hozzá.",
        en: "Owner, assistant, therapist: only the assigned therapist can access medical records.",
      },
    },
    {
      icon: "Sparkles",
      title: { hu: "AI elosztja a pácienseket", en: "AI assigns patients" },
      body: {
        hu: "Új lead: az AI a probléma (váll, derék, sport-sérülés) alapján a megfelelő specialistához irányítja.",
        en: "New lead: AI assigns by problem (shoulder, back, sports injury) to the right specialist.",
      },
    },
    {
      icon: "TrendingUp",
      title: { hu: "Bevétel és havi riport", en: "Revenue and monthly report" },
      body: {
        hu: "Ki mennyit dolgozott, havi bevételi riport CSV exporttal, automatikus jutalék-elszámolás.",
        en: "Who worked how much, monthly revenue report with CSV export, auto commission settlement.",
      },
    },
  ],

  valueProps: [
    {
      icon: "Calendar",
      title: {
        hu: "Kezelés-csomagok és bérletek",
        en: "Treatment bundles and passes",
      },
      body: {
        hu: "4/8/12 alkalmas csomagok, bérletvonás foglaláskor, lejárati emlékeztető: automata számlával.",
        en: "4/8/12-session bundles, pass deduction at booking, expiry reminders: auto-invoiced.",
      },
    },
    {
      icon: "Bell",
      title: { hu: "No-show védelem", en: "No-show protection" },
      body: {
        hu: "Foglaláskor előleg, 24 órás emlékeztető SMS/email/WhatsApp-en, lemondási szabályok: kevesebb üres slot.",
        en: "Deposit at booking, 24-hour SMS/email/WhatsApp reminders, cancellation rules: fewer empty slots.",
      },
    },
    {
      icon: "CreditCard",
      title: {
        hu: "Online + helyszíni fizetés",
        en: "Online + in-person payment",
      },
      body: {
        hu: "Bankkártya, Apple/Google Pay foglaláskor, helyszíni készpénz is: minden tranzakció egy felületen, automata számlával.",
        en: "Card, Apple/Google Pay at booking, in-person cash too: every transaction in one place, auto-invoiced.",
      },
    },
    {
      icon: "Sparkles",
      title: { hu: "AI follow-up kampányok", en: "AI follow-up campaigns" },
      body: {
        hu: "Kontroll-emlékeztetők, inaktív páciens visszahívása automata kampánnyal: te állítod be, a rendszer kiküldi.",
        en: "Check-up reminders, win-back campaigns for inactive patients: you configure, the system sends.",
      },
    },
  ],

  objections: [
    {
      question: {
        hu: "Klinikán dolgozunk több terapeutával: hogy kezeli a csapatot?",
        en: "We work at a clinic with multiple therapists: how does it handle the team?",
      },
      answer: {
        hu: "Minden terapeutának saját naptára, páciens-listája, specialitása van. Te tulajdonosként a centralizált dashboardon mindent látsz, egy felületen. Egészségügyi adatokhoz csak az érintett terapeuta fér hozzá. Korlátlan terapeutát vehetsz fel, NEM fizetsz többet.",
        en: "Each therapist has their own calendar, patient list and specialty. As owner you see everything on the central dashboard. Only the assigned therapist accesses medical data. Add unlimited therapists: you do NOT pay more.",
      },
    },
    {
      question: {
        hu: "Egészségügyi adatokat kezelünk: biztonságos?",
        en: "We handle medical data: is it secure?",
      },
      answer: {
        hu: "Igen. EU-szerverek, GDPR-megfelelőség, end-to-end titkosítás. Jogosultság-kezeléssel csak az érintett terapeuta látja a saját páciensét. Az adatok soha nem kerülnek 3. félhez, AI training-re sem.",
        en: "Yes. EU servers, GDPR-compliant, end-to-end encryption. Role-based access: only the assigned therapist sees their patient. Data is never shared with 3rd parties, never used for AI training.",
      },
    },
    {
      question: {
        hu: "Hogyan működik a számlázás?",
        en: "How does invoicing work?",
      },
      answer: {
        hu: "Minden ülés után automatikus számla készül a Számlázz.hu vagy Billingo fiókodban. Havi bevételi riport CSV-ben exportálható a könyvelődnek.",
        en: "After every session, an invoice is auto-generated in your Számlázz.hu or Billingo account. Monthly revenue report exports to CSV for your accountant.",
      },
    },
    {
      question: {
        hu: "El tudom hozni a meglévő páciens-előzményeket?",
        en: "Can I bring my existing patient records?",
      },
      answer: {
        hu: "Igen. CSV/Excel importtal vagy a Beüzemelés szolgáltatás keretében átvesszük a teljes páciensbázist, korábbi diagnózisokat, kezelési előzményt.",
        en: "Yes. Via CSV/Excel import, or our Setup service migrates the full patient base, past diagnoses and treatment history.",
      },
    },
  ],

  monetization: [
    {
      icon: "Activity",
      title: {
        hu: "Manuálterápia és masszázs",
        en: "Manual therapy and massage",
      },
      examples: [
        { hu: "Manuálterápiás kezelés", en: "Manual therapy session" },
        { hu: "Trigger-pont terápia", en: "Trigger-point therapy" },
        { hu: "Rehabilitációs masszázs", en: "Rehab massage" },
      ],
      priceHint: {
        hu: "Példa: 80 kezelés × 14 000 Ft = 1 120 000 Ft / hó",
        en: "Example: 80 treatments × 14,000 HUF = 1,120,000 HUF / month",
      },
    },
    {
      icon: "Dumbbell",
      title: {
        hu: "Gyógytorna és mozgásterápia",
        en: "Physiotherapy and movement",
      },
      examples: [
        { hu: "1:1 gyógytorna", en: "1:1 physiotherapy" },
        {
          hu: "Kis-csoportos foglalkozás (4 fő)",
          en: "Small-group session (4 ppl)",
        },
        { hu: "Eszközös terápia", en: "Equipment-based therapy" },
      ],
      priceHint: {
        hu: "Példa: 60 torna × 12 000 Ft = 720 000 Ft / hó",
        en: "Example: 60 sessions × 12,000 HUF = 720,000 HUF / month",
      },
    },
    {
      icon: "Heart",
      title: {
        hu: "Sport- és poszt-műtéti rehab",
        en: "Sports and post-op rehab",
      },
      examples: [
        { hu: "Sport-sérülés rehab", en: "Sports injury rehab" },
        { hu: "Posztoperatív rehab", en: "Post-op rehab" },
        {
          hu: "Munkaergonómia konzultáció",
          en: "Workplace ergonomics consult",
        },
      ],
      priceHint: {
        hu: "Példa: 25 sport-rehab × 18 000 Ft = 450 000 Ft / hó",
        en: "Example: 25 sports rehabs × 18,000 HUF = 450,000 HUF / month",
      },
    },
    {
      icon: "BookOpen",
      title: { hu: "Programok és anyagok", en: "Programs and materials" },
      examples: [
        { hu: "8 hetes gerinc-erősítő", en: "8-week spine strengthening" },
        { hu: "Otthoni terápiás csomag", en: "Home therapy bundle" },
        { hu: "Postnatal helyreállító", en: "Postnatal recovery" },
      ],
      priceHint: {
        hu: "Példa: 12 program × 45 000 Ft = 540 000 Ft / hó",
        en: "Example: 12 programs × 45,000 HUF = 540,000 HUF / month",
      },
    },
    {
      icon: "Ticket",
      title: { hu: "Bérletek és tagságok", en: "Passes and memberships" },
      examples: [
        { hu: "10 alkalmas rehab-bérlet", en: "10-session rehab pass" },
        { hu: "Havi karbantartó tagság", en: "Monthly maintenance membership" },
        { hu: "Éves egészség-kártya", en: "Annual health card" },
      ],
      priceHint: {
        hu: "Klinika-szint: 4 terapeuta × 25 bérletes páciens × 32 000 Ft = 3 200 000 Ft / hó potenciál",
        en: "Clinic level: 4 therapists × 25 pass patients × 32,000 HUF = 3,200,000 HUF / month potential",
      },
    },
  ],

  results: [
    {
      value: "+50%",
      label: { hu: "kezelés-sorozat eladás", en: "treatment series sales" },
    },
    {
      value: "-68%",
      label: { hu: "lemondási arány", en: "cancellation rate" },
    },
    {
      value: "4.9★",
      label: { hu: "páciens-elégedettség", en: "patient satisfaction" },
    },
    {
      value: "100%",
      label: { hu: "GDPR-megfelelőség", en: "GDPR compliance" },
    },
  ],
  testimonial: {
    quote: {
      hu: "Az egészségügyi adatok kezelése EU-szerveren történik, pácienseim látják a haladásukat, és a 10-alkalmas csomagok eladása megnőtt 50%-kal.",
      en: "Medical data is stored on EU servers, patients see their progress, and 10-session package sales grew 50%.",
    },
    name: "Dr. Balogh Tamás",
    role: {
      hu: "Gyógytornász, rendelő-tulajdonos",
      en: "Physiotherapist, clinic owner",
    },
    city: { hu: "Veszprém", en: "Veszprém" },
    avatarInitials: "BT",
  },
  howItWorks: [
    {
      step: 1,
      title: {
        hu: "Chatben elmondod a kezeléseid",
        en: "Tell us your treatments in chat",
      },
      body: {
        hu: "Manuálterápia, gerinc-rehabilitáció, sport-sérülés: az AI strukturálja.",
        en: "Manual therapy, spine rehab, sports injury: AI structures it.",
      },
    },
    {
      step: 2,
      title: {
        hu: "AI elkészíti a rendelő oldalt",
        en: "AI builds your clinic site",
      },
      body: {
        hu: "Páciens-kartonok, kezelés-sorozatok, Számlázz.hu integrált számlázás.",
        en: "Patient cards, treatment series, Számlázz.hu invoicing.",
      },
    },
    {
      step: 3,
      title: {
        hu: "Importáljuk a páciens-adatokat",
        en: "We import patient data",
      },
      body: {
        hu: "Anamnézis, korábbi kezelések, képek: GDPR-megfelelően.",
        en: "Anamnesis, past treatments, images: GDPR-compliant.",
      },
    },
    {
      step: 4,
      title: { hu: "Élő, online foglalás", en: "Live, online booking" },
      body: {
        hu: "Kérdőív foglaláskor, automata emlékeztető, követés.",
        en: "Intake form at booking, auto-reminders, follow-up.",
      },
    },
  ],
  integrations: [
    "Számlázz.hu",
    "Billingo",
    "Google Calendar",
    "Stripe",
    "SimplePay",
    "WhatsApp Business",
    "Mailchimp",
  ],
  breadcrumbLabel: {
    hu: "Gyógytornászok és manuálterapeuták",
    en: "Physiotherapists & manual therapists",
  },
  similarPersonas: [
    "massage-therapists",
    "yoga-pilates-instructors",
    "fitness-personal-trainers",
  ],
};
